import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders, getStoredUser, getToken } from '../auth';

const POLL_MS = 15000;

const playNotificationTone = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;

    const beep = (time, freq = 880, duration = 0.13) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, time);
      gain.gain.setValueAtTime(0.001, time);
      gain.gain.exponentialRampToValueAtTime(0.2, time + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, time + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(time);
      osc.stop(time + duration + 0.02);
    };

    beep(now + 0.01, 740);
    beep(now + 0.22, 980);
  } catch {
    // Ignore sound errors (browser policy/device limitations)
  }
};

const maybeShowDesktopNotification = (title, body) => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    // eslint-disable-next-line no-new
    new Notification(title, { body });
  }
};

const InternalOrderNotifier = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getStoredUser();
  const token = getToken();
  const isInternal = user?.role === 'internal' || user?.role === 'admin';
  const initializedRef = useRef(false);
  const seenOrderIdsRef = useRef(new Set());
  const [snack, setSnack] = useState({ open: false, message: '' });

  useEffect(() => {
    if (!isInternal || !token || location.pathname === '/login') return undefined;

    let disposed = false;

    const pollSubmittedOrders = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/orders?orderStatus=Submitted`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) return;
        const data = await response.json();
        const submitted = Array.isArray(data) ? data : [];

        const currentIds = submitted
          .map((order) => String(order?._id || '').trim())
          .filter(Boolean);

        if (!initializedRef.current) {
          currentIds.forEach((id) => seenOrderIdsRef.current.add(id));
          initializedRef.current = true;
          return;
        }

        const newOrders = submitted.filter((order) => {
          const id = String(order?._id || '').trim();
          return id && !seenOrderIdsRef.current.has(id);
        });

        currentIds.forEach((id) => seenOrderIdsRef.current.add(id));
        if (seenOrderIdsRef.current.size > 1000) {
          seenOrderIdsRef.current = new Set(currentIds.slice(0, 500));
        }

        if (!disposed && newOrders.length > 0) {
          const currentUser = getStoredUser() || {};
          const notifyNewOrders = currentUser.notifyNewOrders !== false;
          const notifySound = currentUser.notifySound !== false;
          const notifyDesktop = currentUser.notifyDesktop === true;
          if (!notifyNewOrders) return;

          const newest = newOrders[0];
          const orderNumber = newest?.orderNumber || 'New Order';
          const count = newOrders.length;
          const message =
            count === 1
              ? `New submitted order: ${orderNumber}`
              : `${count} new submitted orders`;

          setSnack({ open: true, message });
          if (notifySound) playNotificationTone();
          if (notifyDesktop) maybeShowDesktopNotification('WTNN Order Alert', message);
        }
      } catch {
        // Silent fail; next poll will retry
      }
    };

    pollSubmittedOrders();
    const intervalId = setInterval(pollSubmittedOrders, POLL_MS);
    return () => {
      disposed = true;
      clearInterval(intervalId);
    };
  }, [isInternal, token, location.pathname]);

  if (!isInternal) return null;

  return (
    <Snackbar
      open={snack.open}
      autoHideDuration={9000}
      onClose={() => setSnack({ open: false, message: '' })}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={() => setSnack({ open: false, message: '' })}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              setSnack({ open: false, message: '' });
              navigate('/orders?status=open');
            }}
          >
            View
          </Button>
        }
      >
        {snack.message}
      </Alert>
    </Snackbar>
  );
};

export default InternalOrderNotifier;
